import React from "react";
import {
  FiSave,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiCheck,
} from "react-icons/fi";
import JoditEditor from "jodit-react";

export default function Step2QuestionsForm({
  questions,
  onQuestionChange,
  onAddQuestion,
  onRemoveQuestion,
  onBack,
  onSave,
  loading,
}) {
  return (
    <div className="h-[75vh] md:h-[80vh] flex flex-col animate-in slide-in-from-right duration-300">
      {/* 1. HEADER - Sticky & Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-blue-50 p-4 rounded-2xl border border-blue-100 mb-4 gap-3">
        <div className="leading-tight">
          <p className="text-blue-700 text-base md:text-lg font-bold">
            Total Questions: {questions.length}
          </p>
          <p className="text-[10px] md:text-xs text-blue-500 uppercase tracking-wider font-semibold">
            Step 2: Configuration
          </p>
        </div>
        <button
          onClick={onAddQuestion}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-md active:scale-95"
        >
          <FiPlus /> <span className="sm:inline">Add Question</span>
        </button>
      </div>

      {/* 2. AREA DE PREGUNTAS - Scrollable */}
      <div className="flex-1 overflow-y-auto pr-1 md:pr-4 space-y-8 custom-scrollbar pb-10">
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="group p-5 md:p-8 bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-200/50 hover:border-blue-200 transition-all relative"
          >
            {/* Header: Numbering & Delete */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-gray-600 text-white rounded-xl flex items-center justify-center font-black shadow-lg shadow-gray-200 text-sm">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">
                    Question Unit
                  </span>
                  <span className="text-xs text-gray-400 font-medium">
                    Configure your question below
                  </span>
                </div>
              </div>

              {questions.length > 1 && (
                <button
                  onClick={() => onRemoveQuestion(idx)}
                  className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all active:scale-95"
                >
                  <FiTrash2 size={20} />
                </button>
              )}
            </div>

            {/* Control Row: Question Type & Options Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-500 uppercase ml-1">
                  Input Format
                </label>
                <div className="flex bg-gray-100/80 p-1 rounded-xl">
                  {["text", "paragraph"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        onQuestionChange(idx, "questionType", type)
                      }
                      className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                        q.questionType === type
                          ? "bg-white text-blue-600 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {type === "text" ? "Plain Text" : "Rich Editor"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-bold text-gray-500 uppercase ml-1">
                  Answer Type
                </label>
                <div className="flex bg-gray-100/80 p-1 rounded-xl">
                  {["text", "image"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => onQuestionChange(idx, "optionsType", type)}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
                        q.optionsType === type
                          ? "bg-white text-emerald-600 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {type === "text" ? "ABCD Text" : "ABCD Images"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Question Text Area */}
            <div className="mb-8">
              {q.questionType === "text" ? (
                <textarea
                  className="w-full p-5 rounded-2xl border-gray-200 border-2 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all outline-none bg-gray-50/30 text-base text-gray-700 font-medium placeholder:text-gray-300"
                  placeholder="Type your question here..."
                  rows={3}
                  value={q.questionText}
                  onChange={(e) =>
                    onQuestionChange(idx, "questionText", e.target.value)
                  }
                />
              ) : (
                <div className="rounded-2xl border-2 border-gray-100 overflow-hidden shadow-inner">
                  <JoditEditor
                    value={q.questionText}
                    onChange={(content) =>
                      onQuestionChange(idx, "questionText", content)
                    }
                  />
                </div>
              )}
            </div>

            {/* Options Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["A", "B", "C", "D"].map((opt) => (
                <div key={opt} className="relative group/opt">
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl border-r transition-all ${
                      q.correctOption === opt
                        ? "bg-emerald-500 text-white border-emerald-600"
                        : "bg-gray-50 text-gray-400 border-gray-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onQuestionChange(idx, "correctOption", opt)
                      }
                      className="w-full h-full font-black text-sm"
                    >
                      {opt}
                    </button>
                  </div>

                  <input
                    type={q.optionsType === "image" ? "url" : "text"}
                    className={`w-full pl-16 pr-4 py-4 rounded-xl border-2 transition-all outline-none text-sm ${
                      q.correctOption === opt
                        ? "border-emerald-500 bg-emerald-50/30 ring-4 ring-emerald-500/5 font-semibold text-emerald-900"
                        : "border-gray-100 focus:border-blue-300 text-gray-600 bg-white"
                    }`}
                    placeholder={
                      q.optionsType === "image"
                        ? "Paste image URL..."
                        : `Option ${opt} content`
                    }
                    value={q.options[opt]}
                    onChange={(e) =>
                      onQuestionChange(idx, `options.${opt}`, e.target.value)
                    }
                  />

                  {q.correctOption === opt && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="bg-emerald-500 rounded-full p-1 shadow-lg shadow-emerald-200">
                        <FiCheck className="text-white w-3 h-3" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Add Question Button */}
        <button
          onClick={onAddQuestion}
          className="w-full py-8 border-2 border-dashed border-blue-100 rounded-[2rem] text-blue-400 font-black hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 hover:shadow-xl hover:shadow-blue-100/50 transition-all flex flex-col items-center justify-center gap-3 group"
        >
          <div className="p-3 bg-blue-50 rounded-full group-hover:scale-110 transition-transform">
            <FiPlus size={24} />
          </div>
          <span className="uppercase tracking-widest text-xs">
            Add New Question
          </span>
        </button>
      </div>

      {/* 3. FOOTER - Botones Responsivos */}
      <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 bg-white">
        <button
          onClick={onBack}
          className="w-full sm:w-auto px-10 py-3.5 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-all active:scale-95 text-sm md:text-base"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={loading}
          className="flex-1 py-3.5 bg-emerald-600 text-white rounded-xl font-bold"
        >
          {loading ? "Saving..." : "Save Questions"}
        </button>
      </div>
    </div>
  );
}
